<template>
  <div
    v-if="reasonForInvalid"
    class="video-instructions-invalid"
    :class="{ 'video-instructions-invalid--no-icon': hideIcon }"
  >
    <div v-if="!hideIcon" class="video-instructions-invalid__icon">
      <div class="video-instructions-invalid__icon-outline-outer"></div>
      <div class="video-instructions-invalid__icon-outline-inner"></div>
      <div class="video-instructions-invalid__icon-inner">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z"
            stroke="#D92D20"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
    <div class="video-instructions-invalid__content">
      <div class="video-instructions-invalid__header">
        <h3
          class="video-instructions-invalid__title"
          :class="{ 'video-instructions-invalid__title--mismatch': isMismatchTitle }"
        >
          {{ invalidTitle }}
        </h3>
      </div>
      <div class="video-instructions-invalid__description">
        <p
          v-for="(paragraph, index) in reasonParagraphs"
          :key="index"
          class="video-instructions-invalid__text"
        >
          {{ paragraph }}
        </p>
      </div>
    </div>
  </div>
  <div
    v-else
    class="video-instructions-summary"
    :class="{ 'video-instructions-summary--editable': editable }"
  >
    <div class="video-instructions-summary__content">
      <div class="video-instructions-summary__header">
        <h3 class="video-instructions-summary__title">{{ title }}</h3>
        <button
          v-if="editable"
          type="button"
          class="video-instructions-summary__icon-btn video-instructions-summary__icon-btn--edit"
          aria-label="Edit summary"
          @click.stop="handleEditClick"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
            class="video-instructions-summary__icon-btn-img"
          >
            <path
              d="M8.75003 2.91403L11.0836 5.24733M12.3518 3.97124C12.6602 3.66291 12.8336 3.24469 12.8336 2.80859C12.8337 2.37249 12.6605 1.95423 12.3521 1.64582C12.0437 1.33741 11.6255 1.16412 11.1893 1.16406C10.7532 1.16401 10.3349 1.3372 10.0264 1.64553L2.24057 9.43231C2.10512 9.56735 2.00495 9.73361 1.94888 9.91647L1.17823 12.4551C1.16315 12.5055 1.16201 12.5591 1.17493 12.6102C1.18785 12.6612 1.21435 12.7078 1.25162 12.745C1.28888 12.7822 1.33552 12.8086 1.38659 12.8215C1.43766 12.8343 1.49125 12.8331 1.54168 12.8179L4.08115 12.0479C4.26385 11.9924 4.43012 11.8928 4.56536 11.758L12.3518 3.97124Z"
              stroke="#344054"
              stroke-width="2"
            />
          </svg>
        </button>
      </div>
      <!-- Two-part VIDEO_WITH_TOC summary: lead sentence + decorative bullet checklist -->
      <div v-if="isTwoPart" class="video-instructions-summary__two-part">
        <p
          v-if="summaryLead"
          class="video-instructions-summary__lead"
        >
          {{ summaryLead }}
        </p>
        <ul
          v-if="summaryBullets.length"
          class="video-instructions-summary__bullets"
        >
          <li
            v-for="(bullet, index) in summaryBullets"
            :key="index"
            class="video-instructions-summary__bullet"
          >
            <span class="video-instructions-summary__check" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.5 7.5L6 10L10.5 4.5"
                  stroke="#6941C6"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="video-instructions-summary__bullet-text">{{ bullet }}</span>
          </li>
        </ul>
      </div>

      <div v-else class="video-instructions-summary__items">
        <div
          v-for="(item, index) in displayItems"
          :key="index"
          class="video-instructions-summary__item"
        >
          <p class="video-instructions-summary__text">
            <span v-if="item.label" class="video-instructions-summary__label"
              >{{ item.label }}:</span
            >
            <span class="video-instructions-summary__description"> {{ item.description }}</span>
          </p>
        </div>
      </div>
    </div>

    <EditSummaryModal
      :modelValue="showEditSummaryModal"
      :summary="editableSummaryText"
      :twoPart="isTwoPart"
      :summaryLead="summaryLead"
      :summaryBullets="summaryBullets"
      :saving="saving"
      @input="onEditSummaryModalInput"
      @update:modelValue="onEditSummaryModalInput"
      @cancel="showEditSummaryModal = false"
      @save="onEditSummarySave"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import EditSummaryModal from "@components/EditSummaryModal/EditSummaryModal.vue";

const props = defineProps({
  title: {
    type: String,
    default: "Summary",
  },
  rubric: {
    type: Object,
    default: () => ({}),
    validator: (value) => {
      return typeof value === "object";
    },
  },
  reasonForInvalid: {
    type: String,
    default: null,
  },
  invalidTitle: {
    type: String,
    default:
      "There is a critical mismatch between the provided business context and the visual content of the video.",
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  editable: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["edit", "save"]);

const showEditSummaryModal = ref(false);

const isMismatchTitle = computed(
  () =>
    String(props.invalidTitle || "")
      .trim()
      .toUpperCase() === "MISMATCH"
);

// Label display names mapping
const labelMap = {
  pass: "Pass",
  borderline: "Borderline",
  fail: "Fail",
};

function overviewTextFromRubric(rubric) {
  if (!rubric || typeof rubric !== "object") return "";
  const nested = rubric.overview ?? rubric.videoSummary;
  if (typeof nested === "string") return nested.trim();
  if (nested && typeof nested === "object") {
    const inner = nested.overview ?? nested.videoSummary;
    if (typeof inner === "string") return inner.trim();
  }
  return "";
}

// Convert rubric object to display items array
const displayItems = computed(() => {
  if (!props.rubric || Object.keys(props.rubric).length === 0) {
    return [];
  }

  const overview = overviewTextFromRubric(props.rubric);
  if (overview) {
    return [{ label: "", description: overview }];
  }

  // Define the order of keys
  const keyOrder = ["pass", "borderline", "fail"];

  return keyOrder
    .filter((key) => props.rubric[key])
    .map((key) => ({
      label: labelMap[key] || capitalizeFirst(key),
      description: props.rubric[key],
    }));
});

/** Plain text for EditSummaryModal — prefer overview, else joined rubric lines. */
const editableSummaryText = computed(() => {
  if (!props.rubric || typeof props.rubric !== "object") return "";
  const overview = overviewTextFromRubric(props.rubric);
  if (overview) return overview;
  return displayItems.value
    .map((item) => (item.label ? `${item.label}: ${item.description}` : item.description))
    .join("\n\n");
});

/**
 * True when the rubric carries the two-part VIDEO_WITH_TOC summary shape:
 * { summary_lead: string, summary_bullets: string[] }.
 */
const isTwoPart = computed(() => {
  const rubric = props.rubric;
  if (!rubric || typeof rubric !== "object" || Array.isArray(rubric)) return false;
  return typeof rubric.summary_lead === "string" || Array.isArray(rubric.summary_bullets);
});

const summaryLead = computed(() => {
  if (!isTwoPart.value) return "";
  return String(props.rubric.summary_lead ?? "").trim();
});

const summaryBullets = computed(() => {
  if (!isTwoPart.value) return [];
  const bullets = props.rubric.summary_bullets;
  if (!Array.isArray(bullets)) return [];
  return bullets
    .map((bullet) => String(bullet ?? "").trim())
    .filter((bullet) => bullet.length > 0);
});

// Split reason_for_invalid into paragraphs
const reasonParagraphs = computed(() => {
  if (!props.reasonForInvalid) {
    return [];
  }
  return props.reasonForInvalid.split("\n\n").filter((p) => p.trim());
});

const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function handleEditClick() {
  if (!props.editable) return;
  emit("edit");
  showEditSummaryModal.value = true;
}

function onEditSummaryModalInput(open) {
  showEditSummaryModal.value = !!open;
}

function onEditSummarySave(payload) {
  emit("save", {
    ...payload,
    complete: (err) => {
      if (!err) showEditSummaryModal.value = false;
    },
  });
}
</script>

<style scoped>
.video-instructions-invalid {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #d0d5dd;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  background-color: #ffffff;
  width: 100%;
  box-sizing: border-box;
}

.video-instructions-invalid__icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-instructions-invalid__icon-outline-outer {
  position: absolute;
  inset: -45%;
  border-radius: 50%;
  border: 2px solid #d92d20;
  opacity: 0.1;
}

.video-instructions-invalid__icon-outline-inner {
  position: absolute;
  inset: -20%;
  border-radius: 50%;
  border: 2px solid #d92d20;
  opacity: 0.3;
}

.video-instructions-invalid__icon-inner {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.video-instructions-invalid__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-instructions-invalid__header {
  padding-right: 32px;
}

.video-instructions-invalid__title {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #000000;
  margin: 0;
}

.video-instructions-invalid__title--mismatch {
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #344054;
}

.video-instructions-invalid--no-icon {
  gap: 0;
}

.video-instructions-invalid__description {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.video-instructions-invalid__text {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  margin: 0;
}

.video-instructions-summary {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #d0d5dd;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  background-color: #ffffff;
  width: 100%;
  box-sizing: border-box;
  transition: background-color 0.15s ease;
}

.video-instructions-summary--editable:hover {
  background-color: #f3f4ff;
}

.video-instructions-summary__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  transition: background-color 0.2s ease;
}

.video-instructions-summary__icon-inner {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-instructions-summary__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.video-instructions-summary__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.video-instructions-summary__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

.video-instructions-summary__icon-btn:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.video-instructions-summary__icon-btn-img {
  display: block;
  flex-shrink: 0;
  pointer-events: none;
}

.video-instructions-summary__icon-btn--edit {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
}

.video-instructions-summary__icon-btn--edit:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #bfc8d4;
}

.video-instructions-summary__title {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  margin: 0;
}

.video-instructions-summary__items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.video-instructions-summary__item {
  width: 100%;
}

.video-instructions-summary__text {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  margin: 0;
}

.video-instructions-summary__label {
  font-weight: 700;
}

.video-instructions-summary__description {
  font-weight: 400;
}

.video-instructions-summary__two-part {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.video-instructions-summary__lead {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #101828;
  margin: 0;
}

.video-instructions-summary__bullets {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.video-instructions-summary__bullet {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.video-instructions-summary__check {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  background-color: #f4ebff;
}

.video-instructions-summary__bullet-text {
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;
  color: #475467;
}
</style>
